import { useState } from 'react';
import { Card, Badge, Row, Col, Button, Offcanvas, Collapse } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { CaseDecisionLog } from '../CaseDecisionLog';
import { personName } from '../../people';
import { enumLabel } from '../../labels';
import { getLang } from '../../language';

export type Case = components['schemas']['Case'];
export type TranslatableText = components['schemas']['TranslatableText'];

export const priorityVariant: Record<string, string> = {
  URGENT: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'secondary',
};

export function fmtDate(s?: string): string {
  return s ? new Date(s).toLocaleString() : '—';
}

export function isAdmin(roles: string[]): boolean {
  return roles.includes('ADMIN') || roles.includes('GLOBAL_ADMIN');
}

/** Render a translatable field: original text plus a machine-translation note if present. */
export function Translatable({ value }: { value?: TranslatableText | null }) {
  if (!value?.original) return <span className="text-muted">—</span>;
  return (
    <>
      <div>{value.original}</div>
      {value.translated && (
        <div className="text-muted fst-italic mt-1">
          {value.translated} <Badge bg="info" className="align-middle">machine translated</Badge>
        </div>
      )}
    </>
  );
}

/** A labelled read-only field. */
export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="text-muted small text-uppercase">{label}</div>
      <div>{children ?? <span className="text-muted">—</span>}</div>
    </div>
  );
}

/** Case title + status/priority badges. Shared header across all layouts. */
export function CaseHeader({ c }: { c: Case }) {
  const lang = getLang();
  return (
    <Card className="shadow-sm mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <Card.Title as="h2" className="h4 mb-1">{c.title}</Card.Title>
            <div className="text-muted">{c.caseId}</div>
          </div>
          <div className="text-end">
            <Badge bg="light" text="dark" className="me-2">{enumLabel('caseStatus', c.status, lang)}</Badge>
            <Badge bg={priorityVariant[c.priority] ?? 'secondary'}>{enumLabel('priority', c.priority, lang)}</Badge>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

/** Patient reference block (body only — caller supplies the Card/section wrapper if desired). */
export function PatientDetails({ c }: { c: Case }) {
  const lang = getLang();
  return (
    <Row>
      <Col sm={6}><Field label="Name">{c.patient?.firstName} {c.patient?.lastName}</Field></Col>
      <Col sm={6}><Field label="Date of birth">{c.patient?.dateOfBirth}</Field></Col>
      <Col sm={6}><Field label="Sex">{enumLabel('sex', c.patient?.sex, lang)}</Field></Col>
      <Col sm={6}><Field label="Country">{c.patient?.country}</Field></Col>
      <Col sm={6}><Field label="Contact">{c.patient?.contactNumber}</Field></Col>
      <Col sm={6}><Field label="Preferred language">{c.patient?.preferredLanguage}</Field></Col>
    </Row>
  );
}

/** Clinical reference block (body only). */
export function ClinicalDetails({ c }: { c: Case }) {
  return (
    <>
      <Field label="Clinical context"><Translatable value={c.clinicalContext} /></Field>
      <Field label="Description"><Translatable value={c.description} /></Field>
      <Field label="Problem duration">{c.problemDuration}</Field>
      <Field label="Medical conditions">{c.medicalConditions}</Field>
      <Field label="Medicines">{c.medicines}</Field>
      <Field label="Allergies">{c.allergies}</Field>
    </>
  );
}

/** Case-meta reference block (specialty/funding/people/dates). */
export function CaseMeta({ c }: { c: Case }) {
  const lang = getLang();
  return (
    <>
      <Field label="Specialty">
        {c.specialtyId ? (c.specialtyName ?? c.specialtyId) : 'Help me choose (to be triaged)'}
      </Field>
      <Field label="Funding status">{enumLabel('fundingStatus', c.fundingStatus, lang)}</Field>
      <Field label="Created by">{personName(c.createdByName, c.createdBy)}</Field>
      <Field label="Assigned to">{c.assignedTo ? personName(c.assignedToName, c.assignedTo) : 'Unassigned'}</Field>
      <Row>
        <Col sm={6}><Field label="Created">{fmtDate(c.createdAt)}</Field></Col>
        <Col sm={6}><Field label="Updated">{fmtDate(c.updatedAt)}</Field></Col>
      </Row>
    </>
  );
}

/** Consent reference block. */
export function ConsentDetails({ c }: { c: Case }) {
  return (
    <>
      <Field label="Consent version">{c.consent?.consentTextVersion}</Field>
      <Field label="Recorded by">{personName(c.consent?.recordedByName, c.consent?.recordedBy)}</Field>
      <Field label="Recorded at">{fmtDate(c.consent?.recordedAt)}</Field>
    </>
  );
}

/**
 * On-demand, admin-gated activity/decision log. Renders nothing for non-admins. For admins it
 * shows a button that opens the decision log in an Offcanvas drawer (pulled on open), so the
 * audit trail never competes with the clinical workspace. Shared by all layouts.
 */
export function ActivityLog({
  caseId,
  roles,
  refreshKey,
}: {
  caseId: string;
  roles: string[];
  refreshKey?: number | string;
}) {
  const [open, setOpen] = useState(false);
  if (!isAdmin(roles)) return null;
  return (
    <>
      <Button variant="outline-secondary" size="sm" onClick={() => setOpen(true)}>
        View activity log
      </Button>
      <Offcanvas show={open} onHide={() => setOpen(false)} placement="end" style={{ width: 480 }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Activity log</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {open && <CaseDecisionLog caseId={caseId} refreshKey={refreshKey} />}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

/** A titled collapsible section wrapper (used by the accordion variant + collapsible reference). */
export function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="shadow-sm mb-3">
      <Card.Header
        role="button"
        onClick={() => setOpen((o) => !o)}
        className="d-flex justify-content-between align-items-center"
      >
        <span>{title}</span>
        <span className="text-muted small">{open ? '▾' : '▸'}</span>
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body>{children}</Card.Body>
        </div>
      </Collapse>
    </Card>
  );
}
